import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Check } from "lucide-react";

// Sovereign Estate Bridge — signed advisories received from Umbrella Command.
// Advisory only: nothing here can move money or mutate payroll.
export const BridgeMandates = () => {
  const [mandates, setMandates] = useState([]);
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([
        api.get("/bridge/mandates?status=open"),
        api.get("/bridge/status"),
      ]);
      setMandates(m.data.mandates || []);
      setStatus(s.data);
      try {
        const ev = await api.get("/bridge/events");
        setEvents(ev.data.events || []);
      } catch { /* events are informational */ }
    } catch { /* bridge is optional — never block the dashboard */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ack = async (id) => {
    try {
      await api.post(`/bridge/mandates/${id}/acknowledge`);
      toast.success("Directive acknowledged");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!status?.secret_configured && mandates.length === 0) return null;

  const PRIORITY = {
    critical: "border-bad text-bad", high: "border-bad text-bad",
    normal: "border-ink text-ink", low: "border-ink text-muted-foreground",
  };

  return (
    <div className="hard-card p-5 mb-8" data-testid="bridge-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-warn" />
          <div className="overline">§ SOVEREIGN ESTATE BRIDGE</div>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="bridge-status-line">
          {status?.last_pulse_at
            ? `Last pulse ${new Date(status.last_pulse_at).toLocaleString()} · ${status.last_pulse_delivered ? "delivered" : "not delivered"}`
            : "No pulse emitted yet"}
        </div>
      </div>
      {status && status.last_pulse_delivered === false && (
        <div className="mb-3 border-2 border-warn bg-warn/10 p-3 font-mono text-[11px] leading-relaxed" data-testid="bridge-handshake-warning">
          Umbrella Command refused the last pulse{status.last_pulse_response ? ` — ${status.last_pulse_response}` : ""}.
          The protocol is wired ({status.pulse_path} · app <b>{status.bridge_app_id}</b> · HMAC over
          {" "}<code>&lt;app&gt;.&lt;iso-ts&gt;.&lt;body&gt;</code>); this clears once the Commander's
          BRIDGE_SHARED_SECRET is installed here.
        </div>
      )}
      {mandates.length === 0 ? (
        <div className="font-mono text-xs text-muted-foreground" data-testid="bridge-empty">
          Bridge armed — no open directives from Umbrella Command.
        </div>
      ) : (
        <ul className="divide-y divide-ink/20" data-testid="bridge-mandate-list">
          {mandates.map((m) => (
            <li key={m.id} className="py-3 flex items-start justify-between gap-4" data-testid={`bridge-mandate-${m.id}`}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`border-2 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${PRIORITY[m.priority] || PRIORITY.normal}`}>
                    {m.priority}
                  </span>
                  <span className="font-black">{m.title}</span>
                  {m.due_date && <span className="font-mono text-[10px] text-muted-foreground">due {m.due_date}</span>}
                </div>
                {m.directive && <div className="font-mono text-xs text-muted-foreground mt-1 leading-relaxed">{m.directive}</div>}
              </div>
              <button onClick={() => ack(m.id)} data-testid={`bridge-ack-${m.id}`} className="shrink-0 border-2 border-ink px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-1 hover:bg-ink hover:text-white">
                <Check className="w-3 h-3" /> Acknowledge
              </button>
            </li>
          ))}
        </ul>
      )}
      {events.length > 0 && (
        <div className="mt-4 border-t-2 border-ink pt-3" data-testid="bridge-events">
          <div className="overline mb-2">Outbound pings</div>
          <ul className="font-mono text-[11px] space-y-1">
            {events.slice(0, 5).map((ev) => (
              <li key={ev.id} className="flex items-center justify-between gap-3" data-testid={`bridge-event-${ev.id}`}>
                <span>
                  {ev.kind === "employee_anniversary"
                    ? `${ev.employee_name} — ${ev.years_of_service} year work anniversary${ev.employer ? ` · ${ev.employer}` : ""}`
                    : ev.kind}
                </span>
                <span className={ev.delivered ? "text-good" : "text-muted-foreground"}>
                  {ev.delivered ? "sent" : "queued / undelivered"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
